import { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Cpu, PenTool, Plus, Sparkles, TrendingUp } from "lucide-react";
import { EASE, FadeUp, MaskLines } from "./Reveal";

const COLS = [
  {
    h: "Experience Design",
    icon: PenTool,
    gradient: "linear-gradient(135deg, #1E3BD6 0%, #7FB2FF 100%)",
    desc: "Designs that make complex products feel effortless — across web, mobile and devices.",
    items: [
      { t: "UI/UX Design", d: "Interfaces users don't have to think about." },
      { t: "Website Design", d: "Sites that sell while you sleep." },
      { t: "Mobile Experience", d: "Thumb-friendly flows for small screens." },
      { t: "Commerce Experience", d: "Storefronts built to convert browsers." },
      { t: "Human Machine Interface (HMI)", d: "Touchscreens for machines and devices." },
      { t: "Applications and Dashboards", d: "Your whole operation on one screen." },
      { t: "Wearable App Design", d: "Glanceable design for wrists and rings." },
      { t: "Cross-Platform Design", d: "One design language, every device." },
      { t: "Design Support and Maintenance", d: "Your product evolves after launch." },
    ],
  },
  {
    h: "Technology",
    icon: Cpu,
    gradient: "linear-gradient(135deg, #0F0F14 0%, #4B66F0 100%)",
    desc: "Engineering that ships fast, scales cleanly and stays secure.",
    items: [
      { t: "AI & Machine Learning", d: "Models that learn your business." },
      { t: "Web Development", d: "Fast, scalable, production-grade builds." },
      { t: "Mobile App Development", d: "Native-feel apps for iOS and Android." },
      { t: "E-commerce", d: "Checkout flows that just work." },
      { t: "DevOps Consulting", d: "Ship faster with bulletproof pipelines." },
      { t: "Data & Analytics", d: "Decisions backed by live numbers." },
      { t: "Cloud Services", d: "Infrastructure that scales with you." },
      { t: "Cyber Security", d: "Locked down, audited, always on." },
      { t: "Quality Assurance & Testing", d: "Bugs caught before your users do." },
    ],
  },
  {
    h: "Digital Marketing",
    icon: TrendingUp,
    gradient: "linear-gradient(135deg, #5B21B6 0%, #C084FC 100%)",
    desc: "Full-funnel marketing wired to your CRM and your numbers.",
    items: [
      { t: "Social Media Management", d: "Channels that grow communities." },
      { t: "Performance Marketing", d: "Every rupee tracked to a result." },
      { t: "Search Engine Optimisation", d: "Rank for what your buyers search." },
      { t: "Content Marketing", d: "Stories that earn attention." },
      { t: "Marketing Automation", d: "Journeys that run while you sleep." },
      { t: "Analytics", d: "Know exactly what's working." },
    ],
  },
  {
    h: "Creative & Tools",
    icon: Sparkles,
    gradient: "linear-gradient(135deg, #EA580C 0%, #F43F5E 100%)",
    desc: "Content and internal tools that keep your brand moving.",
    items: [
      { t: "Short Animation Videos", d: "Scroll-stopping motion in seconds." },
      { t: "UI & UX Builder", d: "Interactive prototypes, fast." },
      { t: "Calendar & Scheduling Solutions", d: "Booking flows without the back-and-forth." },
      { t: "Dentist Reception Calling & Appointments", d: "Front-desk calls, fully automated." },
    ],
  },
];

export default function EveryBusiness() {
  const [open, setOpen] = useState(0);
  const [active, setActive] = useState(null);
  const [hasHover, setHasHover] = useState(false);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 170, damping: 22 });
  const sy = useSpring(my, { stiffness: 170, damping: 22 });

  useEffect(() => {
    setHasHover(window.matchMedia("(hover: hover) and (pointer: fine)").matches);
  }, []);

  const track = (e) => {
    const cardW = 300;
    const cardH = 214;
    let x = e.clientX + 26;
    if (x + cardW > window.innerWidth - 12) x = e.clientX - cardW - 26;
    let y = e.clientY + 26;
    if (y + cardH > window.innerHeight - 12) y = e.clientY - cardH - 26;
    mx.set(x);
    my.set(y);
  };

  return (
    <section
      id="services"
      className="scroll-mt-20 bg-paper pb-28 pt-4 text-[#0f0f14]"
      onMouseLeave={() => setActive(null)}
    >
      <div className="mx-auto max-w-[1160px] px-6">
        <FadeUp>
          <p
            className="text-[13px] font-semibold uppercase tracking-[0.18em] text-brand"
            data-testid="every-business-eyebrow"
          >
            02 — Every Business
          </p>
        </FadeUp>

        <MaskLines
          lines={["Everything else your", "business runs on."]}
          className="mt-8 text-[clamp(2.5rem,5.4vw,4.6rem)] font-normal leading-[1.04] tracking-[-0.025em]"
        />

        <FadeUp delay={0.1}>
          <p className="mt-8 max-w-[560px] text-[17px] leading-[1.65] text-[#54545c]">
            The same team, the same engineering discipline — applied to design, software,
            marketing and AI for 20+ industries.
          </p>
        </FadeUp>

        <div className="mt-16">
          {COLS.map((col, i) => {
            const isOpen = open === i;
            const Icon = col.icon;
            return (
              <FadeUp key={col.h} delay={i * 0.05}>
                <div className="border-t border-black/[0.12]">
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    data-testid={`service-pillar-${i + 1}`}
                    className="group flex w-full items-center gap-5 py-7 text-left md:gap-8"
                  >
                    <span className="text-[14px] font-medium text-brand">0{i + 1}</span>
                    <Icon className="hidden h-6 w-6 text-[#0f0f14]/55 transition-colors duration-300 group-hover:text-brand sm:block" />
                    <span className="flex-1 text-[clamp(1.5rem,2.6vw,2.4rem)] font-medium tracking-[-0.015em] transition-colors duration-300 group-hover:text-brand">
                      {col.h}
                    </span>
                    <span className="hidden rounded-full border border-black/[0.09] bg-white px-3.5 py-1 text-[13px] font-medium text-[#54545c] md:block">
                      {col.items.length} services
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
                        isOpen ? "border-brand bg-brand text-white" : "border-black/[0.12] text-[#0f0f14]"
                      }`}
                    >
                      <Plus className="h-5 w-5" />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        className="overflow-hidden"
                        data-testid={`service-panel-${i + 1}`}
                      >
                        <div className="pb-10 pl-0 md:pl-[52px]">
                          <p className="max-w-[560px] text-[16px] leading-[1.6] text-[#54545c]">
                            {col.desc}
                          </p>
                          <div className="mt-7 flex flex-wrap gap-3">
                            {col.items.map((item) => (
                              <button
                                key={item.t}
                                type="button"
                                onMouseEnter={(e) => {
                                  if (!hasHover) return;
                                  track(e);
                                  sx.jump(mx.get());
                                  sy.jump(my.get());
                                  setActive({ col, item });
                                }}
                                onMouseMove={track}
                                onMouseLeave={() => setActive(null)}
                                data-testid={`service-chip-${item.t
                                  .toLowerCase()
                                  .replace(/[^a-z]+/g, "-")}`}
                                className="rounded-full border border-black/[0.09] bg-white px-4 py-2.5 text-[15px] font-medium transition-all duration-200 hover:border-brand hover:text-brand"
                              >
                                {item.t}
                              </button>
                            ))}
                          </div>
                          <a
                            href="#contact"
                            data-testid={`services-explore-${col.h
                              .toLowerCase()
                              .replace(/[^a-z]+/g, "-")}`}
                            className="group mt-8 inline-flex items-center gap-2 text-[17px] font-medium text-brand transition-colors duration-200 hover:text-brand-deep"
                          >
                            Explore this pillar
                            <span className="transition-transform duration-300 group-hover:translate-x-1">
                              →
                            </span>
                          </a>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </FadeUp>
            );
          })}
          <div className="border-t border-black/[0.12]" />
        </div>
      </div>

      {hasHover && (
        <AnimatePresence>
          {active && (
            <motion.div
              key={active.item.t}
              initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.9, rotate: 2 }}
              transition={{ duration: 0.28, ease: EASE }}
              style={{ x: sx, y: sy }}
              className="pointer-events-none fixed left-0 top-0 z-40 h-[214px] w-[300px] overflow-hidden rounded-2xl text-white shadow-[0_24px_60px_rgba(10,10,14,0.35)]"
              data-testid="service-preview-card"
            >
              <div className="absolute inset-0" style={{ background: active.col.gradient }} />
              <active.col.icon className="absolute -bottom-8 -right-8 h-40 w-40 text-white/15" />
              <div className="relative flex h-full flex-col justify-between p-6">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/70">
                  {active.col.h}
                </p>
                <div>
                  <h4 className="text-[22px] font-medium leading-[1.15] tracking-[-0.01em]">
                    {active.item.t}
                  </h4>
                  <p className="mt-2 text-[14px] leading-[1.5] text-white/80">{active.item.d}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </section>
  );
}
